<style scoped lang="scss">
    .select-wrapper {
        label {
            padding-left: 10px;
        }
        input {
            margin-left: 20px;
        }
    }
    .contact-info-field {
        display: flex;
        align-items: center;
        margin-bottom: 10px;
        label {
            display: inline-block;
            width: 330px;
        }
        .text-input {
            width: 265px;
            display: block;
        }
    }
</style>

<template>
    <div class="uk-form uk-width-1-1">
        <div class="uk-grid">
            <div class="uk-width-1-1">
                <h1 class="uk-margin-top">
                    Written Attestation of Income
                </h1>
                <h2 class="uk-margin-large-bottom">Each household member 18 years or older, who cannot provide supporting documentation for their income due to extenuating circumstances related to the pandemic, disabilities, lack of technological access or if the income was received in cash must complete this form.</h2>
                <p><b>Household Member's Name:</b> {{ label }}</p>
                <p><b>Household Applicant's Phone:</b> {{ applicantPhone }}</p>
                <p><b>Property Address:</b> {{ propertyAddress }}</p>
            </div>
            <div class="uk-width-1-1 uk-margin-top">
                <p><b>Within the last {{ programSettings['required-days-of-income-proof'] }} days, did you receive income from any of the following sources?</b></p>
                <table class="uk-table uk-table-striped">
                    <thead>
                    <tr>
                        <th>Income Type</th>
                        <th>Interval</th>
                        <th>Amount</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(income, idx) in incomes" v-if="income.doc_type === 'attestation'">
                        <td width="70%">{{ income.label }}</td>
                        <td class="uk-text-capitialize">{{ paymentIntervals[income.interval] }}</td>
                        <td>
                            <div v-for="(item, index) in income.attestations">
                                <div class="uk-flex uk-margin-small-bottom">
                                    <span>$ {{ item.income_amount }}</span>
                                </div>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </table>
                <p class="uk-text-right uk-text-bold">Total Annualized Income: ${{ totalIncomeRequest }}</p>
                <template v-for="(item, idx) in incomes" v-if="item.doc_type === 'attestation'">
                    <div class="contact-info-wrapper" v-if="item.require_interval && getIncomeAmount(item)">
                        <h4>{{ item.label }}</h4>
                        <div class="contact-info-field">
                            <label>Name of Employer / Former Employer:</label>
                            <template v-if="!isAdditionalInfoEditable">
                                <span>{{item.additional_info.employer_name}}</span>
                            </template>
                            <template v-else>
                            <validated-section :field="`employer_name_${idx}`" :errors="errors">
                                <TextInput
                                    name="Name of Employer"
                                    v-model="item.additional_info.employer_name"
                                    class="text-input"
                                    @change="onInputChage"
                                ></TextInput>
                            </validated-section>
                            </template>
                        </div>
                        <div class="contact-info-field">
                            <label>Name of Contact Person to Verify Employment:</label>
                            <template v-if="!isAdditionalInfoEditable">
                                <span>{{item.additional_info.contact_name}}</span>
                            </template>
                            <template v-else>
                            <validated-section :field="`contact_name_${idx}`" :errors="errors">
                                <TextInput
                                    name="Name of Contact Person"
                                    v-model="item.additional_info.contact_name"
                                    class="text-input"
                                    @change="onInputChage"
                                ></TextInput>
                            </validated-section>
                            </template>
                        </div>
                        <div class="contact-info-field">
                            <label>Phone Number of Contact Person:</label>
                            <template v-if="!isAdditionalInfoEditable">
                                <span>{{item.additional_info.contact_phone}}</span>
                            </template>
                            <template v-else>
                            <validated-section :field="`contact_phone_${idx}`" :errors="errors">
                                <PhoneNumberInput
                                    name="Phone Number of Contact Person: "
                                    v-model="item.additional_info.contact_phone"
                                    class="text-input"
                                    @onInputChange="onInputChage"
                                ></PhoneNumberInput>
                            </validated-section>
                            </template>
                        </div>
                    </div>
                </template>
            </div>

            <div class="uk-width-1-1 uk-margin-large-top">
                <p>Under penalty of perjury, I certify that the information presented in this certification is true and accurate to the best of my knowledge.  I further understand that providing false representations constitutes an act of fraud. False, misleading, or incomplete information may result in the repayment of any funds received through the <b>{{ program['program_abbreviation'] }}</b> Program and other remedies available under applicable law.  I also give the <b>{{ program['agency_short_name'] }}</b> and its partners permission to obtain a copy of any tax returns from the Internal Revenue Service and to verify income and other information provided herein from other State agencies.</p>
                <div class="uk-width-1-1 uk-margin-top" v-if="programSettings['uses-esign']">
                    <validated-section field="signature_no_income" :errors="errors">
                        <signature-box @updated="onSignatureUpdated"
                                       :user-key="member.id"
                                       :is-signed="signatureData[member.id] && signatureData[member.id].signed_at !== null"
                                        :completed="signatureData[member.id] && signatureData[member.id].completed"
                                       :name="member.first_name + ' ' + member.last_name"
                                       :color="programSettings['primary-program-color']"
                                       ref="signatureBox"
                        ></signature-box>
                    </validated-section>
                </div>
                <div v-else>
                    <DocRequest
                        :applicationId="application.id"
                        :member="member"
                        :incomes="incomes"
                        docType="no-income"
                        :income="income"
                        :additionalInfo="additionalInfo"
                        :showCancel="true"
                        @onCancel="onCancel"
                        @onDownload="onDownload"
                    ></DocRequest>
                </div>
            </div>

            <div class="uk-width-1-1 uk-margin-top uk-text-center" v-if="programSettings['uses-esign']">
                <a href="javascript:;" class="uk-button uk-button-default" @click="onCancel">Cancel</a>
                <a href="javascript:;" class="uk-button uk-button-primary" @click="onSubmit">Submit</a>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters, mapState } from 'vuex';
    import moment from 'moment';
    import ValidatedSection from "../../../components/Common/ValidatedSection";
    import SignatureBox from "../SignatureBox";
    import ModalDialog from "../../../components/Common/ModalDialog";
    import DocRequest from "./DocRequest";

    export default {
        name: "NoIncomeForm",
        props: {
            label: {
                type: String,
                default: ''
            },
            member: {
                type: Object,
                default: {}
            },
            members: {
                type: Array,
                default: []
            },
            url: {
                type: String,
                default: ''
            },
            incomes: {
                type: Array,
                default: []
            },
            incomeIndex: {
                type: Number,
                default: undefined
            },
        },
        components: {
            ValidatedSection,
            SignatureBox,
            ModalDialog,
            DocRequest
        },
        computed: {
            ...mapState('Application', {
                application: 'application',
                applicant: 'applicant',
            }),

            ...mapGetters('Application', [ 'program', 'programSettings' ]),

            applicantPhone() {
                return this.applicant.primary_contact ? this.applicant.primary_contact.number : ''
            },

            propertyAddress() {
                let result = ''
                if (this.applicant.property_address)
                {
                    result = `${this.applicant.property_address.line_1} ${this.applicant.property_address.line_2}, ${this.applicant.property_address.city}, ${this.applicant.property_address.state}, ${this.applicant.property_address.zip}`
                }
                return result
            },
            isAdditionalInfoEditable() {
                return true
            },

            additionalInfo() {
                if (this.incomes[this.incomeIndex] && this.incomes[this.incomeIndex].additional_info)
                    return this.incomes[this.incomeIndex].additional_info
                return {}
            },
            attestableIncomes() {
                return this.incomes;
            },

            totalIncomeRequest() {
                let total = 0
                _.each(this.incomes, (item) => {
                    total += this.getAnnualIncomeFromIncomeValue(item)
                })
                return total.toFixed(2)
            }
         },
        data() {
            return {
                errors: {},
                signatureData: {},
                paymentIntervals: {
                    monthly: 'Monthly',
                    weekly: 'Weekly',
                    biweekly: 'Biweekly (every other week)',
                    bimonthly: 'Bimonthly (twice a month)',
                    annually: 'Annually',
                    other: 'Other (enter income for last 30 days)'
                },
            }
        },
        watch: {
            application: {
                handler: function (newValue) {
                    if(newValue.household) {
                        this.initData();
                    }
                },
                deep: true
            },
        },
        mounted() {
        },
        methods: {
            initData() {
                this.signatureData = {}
                const signatureFiltered = this.member.signatures.find(item => item.key === `no-income`)

                if(signatureFiltered) {
                    this.$set(this.signatureData, this.member.id, { signed_at: signatureFiltered.created_at, completed: true });
                }
            },

            onSignatureUpdated($event) {
                if(!this.signatureData[$event.key]) {
                    this.$set(this.signatureData, $event.key, {});
                }
                this.$set(this.signatureData, $event.key, { signed_at: $event.isSigned ? moment().format('YYYY-MM-DD HH:mm') : null});
                this.validate()
            },

            onDownload() {
                this.$emit('onSubmitForm', {
                    signature: this.signatureData,
                    incomesData: this.incomes,
                    closeModal: false
                });
            },

            onSubmit() {
                const isValid = this.validate();
                if (isValid)
                    this.$emit('onSubmitForm', {
                        signature: this.signatureData,
                        incomesData: this.incomes,
                        closeModal: true
                    });
            },

            onCancel() {
                this.$emit('onCancelForm');
            },

            onChangeRadio(index) {
                if (this.incomes[index].val === 1)
                {
                    this.$refs.confirmModal.open();
                }
            },

            getIncomeAmount(income) {
                let assets = [];
                let value = 0;
                if (income.doc_type === 'paystub')
                {
                    assets = income.paystubs
                } else {
                    assets = income.attestations
                }

                value = _.reduce(assets, (sum, item) => {
                    return sum + item.income_amount
                }, 0);

                value = assets.length ? value / assets.length : 0;

                return value.toFixed(2)
            },

            getAnnualIncomeFromIncomeValue(income) {
                let amount;
                let value = this.getIncomeAmount(income)

                switch(income.interval) {
                    case 'monthly':
                    case 'other':
                        amount = parseFloat(value) * 12;
                        break;

                    case 'weekly':
                        amount = parseFloat(value) * 52;
                        break;

                    case 'biweekly':
                        amount = parseFloat(value) * 26;
                        break;

                    case 'bimonthly':
                        amount = parseFloat(value) * 24;
                        break;

                    case 'annually':
                        amount = parseFloat(value);
                        break;

                    default: amount = parseFloat(value) * 12;
                }

                return amount ? amount : 0;

            },

            validate() {
                console.log('xxx')
                this.errors = {}
                let stepValid = true;

                if (this.programSettings['uses-esign'])
                {
                    if(!this.signatureData[this.member.id]) {
                        this.errors['signature_no_income'] = {
                            errors: [this.member.first_name + ' ' + this.member.last_name + ' must sign this form as their name appears above.'],
                        };
                    }else if(this.signatureData[this.member.id] && this.signatureData[this.member.id].signed_at == null) {
                        this.errors['signature_no_income'] = {
                            errors: [this.member.first_name + ' ' + this.member.last_name + ' must sign this form as their name appears above.'],
                        };
                    }
                }

                _.each(this.incomes, (item, index) => {
                    if (item.require_interval && this.getAnnualIncomeFromIncomeValue(item) && item.doc_type === 'attestation')
                    {
                        if (!item.additional_info.employer_name)
                        {
                            this.errors[`employer_name_${index}`] = {
                                errors: ['Please input this value.'],
                            };
                        }

                        if (!item.additional_info.contact_name)
                        {
                            this.errors[`contact_name_${index}`] = {
                                errors: ['Please input this value.'],
                            };
                        }

                        if (!item.additional_info.contact_phone)
                        {
                            this.errors[`contact_phone_${index}`] = {
                                errors: ['Please input this value.'],
                            };
                        }
                    }
                })

                let errorCount = _.size(this.errors);

                if(errorCount > 0) stepValid = false;

                return stepValid
            },
        }
    }
</script>
